import type { Core } from 'clarity-js'
import { urlFor } from '~/core/tuyau'

// Clarity interface based on clarity-js types
type ClarityInstance = {
  start: (config?: Core.Config) => void
  stop: () => void
  pause: () => void
  resume: () => void
  event: (name: string, value: string) => void
  set: (variable: string, value: string | string[]) => void
  identify: (userId: string, sessionId?: string, pageId?: string, userHint?: string) => void
}

export type CookiePreferences = {
  essential: boolean
  analytics: boolean
}

export const DEFAULT_COOKIE_PREFERENCES: CookiePreferences = {
  essential: true, // Always true, cannot be disabled
  analytics: false,
}

const COOKIE_CONSENT_KEY = 'sota_cookie_consent'
const COOKIE_PREFERENCES_KEY = 'sota_cookie_preferences'

export class CookieConsentManager {
  static hasConsent(): boolean {
    if (typeof window === 'undefined') return false
    return localStorage.getItem(COOKIE_CONSENT_KEY) === 'true'
  }

  static setConsent(granted: boolean): void {
    if (typeof window === 'undefined') return
    localStorage.setItem(COOKIE_CONSENT_KEY, granted.toString())
  }

  static getPreferences(): CookiePreferences {
    if (typeof window === 'undefined') return DEFAULT_COOKIE_PREFERENCES

    const stored = localStorage.getItem(COOKIE_PREFERENCES_KEY)
    if (!stored) return DEFAULT_COOKIE_PREFERENCES

    try {
      const parsed = JSON.parse(stored) as CookiePreferences
      // Ensure essential is always true and provide defaults for new properties
      return {
        essential: true,
        analytics: parsed.analytics ?? false,
      }
    } catch {
      return DEFAULT_COOKIE_PREFERENCES
    }
  }

  static setPreferences(preferences: CookiePreferences): void {
    if (typeof window === 'undefined') return

    // Ensure essential is always true
    const safePreferences = { ...preferences, essential: true }
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(safePreferences))

    // Also set consent as granted when preferences are saved
    CookieConsentManager.setConsent(true)
  }

  static acceptAll(): void {
    const allAccepted: CookiePreferences = {
      essential: true,
      analytics: true,
    }
    CookieConsentManager.setPreferences(allAccepted)
  }

  static rejectAll(): void {
    const onlyEssential: CookiePreferences = {
      essential: true,
      analytics: false,
    }
    CookieConsentManager.setPreferences(onlyEssential)

    // Stop Clarity tracking when analytics are rejected
    if (typeof window !== 'undefined') {
      ClarityManager.getInstance().stop()
    }
  }

  static clearConsent(): void {
    if (typeof window === 'undefined') return
    localStorage.removeItem(COOKIE_CONSENT_KEY)
    localStorage.removeItem(COOKIE_PREFERENCES_KEY)

    // Stop tracking when consent is cleared
    ClarityManager.getInstance().stop()
  }
}

// Helper functions for external services
export const initializeAnalytics = (): void => {
  if (typeof window === 'undefined') return

  // Check if user has consented to analytics cookies
  const preferences = CookieConsentManager.getPreferences()
  if (!preferences.analytics) return

  // Get Microsoft Clarity project ID from Vite environment variable
  const clarityProjectId = import.meta.env.VITE_MICROSOFT_CLARITY_PROJECT_ID

  if (!clarityProjectId) {
    console.warn('Microsoft Clarity project ID not configured')
    return
  }
  console.log('Initializing Microsoft Clarity with project ID:', clarityProjectId)

  // Initialize Clarity using clarity-js package with proxy configuration
  import('clarity-js').then(({ clarity }) => {
    const uploadUrl = urlFor('clarity.collect')

    clarity.start({
      projectId: clarityProjectId,
      upload: uploadUrl,
      track: true,
      content: true,
    })
    // Store clarity instance in singleton for later use
    ClarityManager.getInstance().setInstance(clarity)

    // Set custom variables for better tracking after initialization
    setTimeout(() => {
      // Set custom variables for better tracking
      clarity.set('platform', 'SOTA')
      clarity.set('environment', import.meta.env.MODE || 'production')
      // Track initialization event
      clarity.event('analytics_initialized', 'clarity')
    }, 100)
  })
}

// Clarity Manager Singleton
class ClarityManager {
  private static instance: ClarityManager
  private clarityInstance: ClarityInstance | null = null

  private constructor() {}

  static getInstance(): ClarityManager {
    if (!ClarityManager.instance) {
      ClarityManager.instance = new ClarityManager()
    }
    return ClarityManager.instance
  }

  setInstance(clarityInstance: ClarityInstance): void {
    this.clarityInstance = clarityInstance
  }

  isInitialized(): boolean {
    return this.clarityInstance !== null
  }

  identify(userId: string, sessionId?: string, userHint?: string): void {
    if (this.clarityInstance) {
      this.clarityInstance.identify(userId, sessionId, undefined, userHint)
    }
  }

  event(name: string, value: string): void {
    if (this.clarityInstance) {
      this.clarityInstance.event(name, value)
    }
  }

  set(variable: string, value: string | string[]): void {
    if (this.clarityInstance) {
      this.clarityInstance.set(variable, value)
    }
  }

  stop(): void {
    if (this.clarityInstance) {
      this.clarityInstance.stop()
    }
  }

  pause(): void {
    if (this.clarityInstance) {
      this.clarityInstance.pause()
    }
  }

  resume(): void {
    if (this.clarityInstance) {
      this.clarityInstance.resume()
    }
  }
}

// Export ClarityManager for direct use
export { ClarityManager }
